import { CmsSchema } from '@/cms/content';
import { serializeCmsJson } from '@workspace/url-source/cms';
import { useCmsFaq } from '@/components/cms/useCmsFaq';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Warsztaty florystyczne — /warsztaty-florystyczne
 * Strona sprzedażowo-informacyjna, editorial lifestyle.
 * Formularz bez backendu — struktura gotowa do integracji.
 */
import { useState, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'wouter';
import { useInView } from '@/lib/useInView';
import {
  ArrowRight, ArrowDown, Scissors, Flower2, Leaf, Package,
  Circle, Star, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { FaqAccordionItem } from '@/components/FaqAccordionItem';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { isValidInternationalPhone, phoneValidationMessage } from '@/lib/phone';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { buildFaqJsonLd } from '@/lib/faqJsonLd';
import { WARSZTATY_CONTENT } from '@/data/warsztatyContent';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';
const BROWN  = '#5a3e28';


/* ── Shared primitives ───────────────────────────────────── */
const ICON_MAP: Record<string, LucideIcon> = {
  flower2: Flower2, package: Package, circle: Circle, leaf: Leaf,
  star: Star, scissors: Scissors, checkCircle2: CheckCircle2,
};

/* Staggered reveal delays for the editorial gallery masonry, matched
   to the visual row order rather than array order. */

function H2({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.9rem,3.2vw,3rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.08, ...style }}>
      {children}
    </h2>
  );
}

/** Cream-coloured photo placeholder */

/* ── Form helpers ────────────────────────────────────────── */
const fieldBase: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif', fontSize: 14.5, color: DARK,
  background: '#fff', border: `1px solid ${LINE}`,
  padding: '12px 15px', outline: 'none',
};

function FInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [f, setF] = useState(false);
  return <input {...props} style={{ ...fieldBase, borderColor: f ? GOLD : LINE }} onFocus={() => setF(true)} onBlur={() => setF(false)} />;
}
function FSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const [f, setF] = useState(false);
  return <select {...props} style={{ ...fieldBase, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23c4a35a' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 13px center', paddingRight: 34, borderColor: f ? GOLD : LINE }} onFocus={() => setF(true)} onBlur={() => setF(false)} />;
}
function FTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [f, setF] = useState(false);
  return <textarea {...props} style={{ ...fieldBase, resize: 'vertical', minHeight: 100, borderColor: f ? GOLD : LINE }} onFocus={() => setF(true)} onBlur={() => setF(false)} />;
}
function FL({ children, req }: { children: React.ReactNode; req?: boolean }) {
  return <label style={{ display: 'block', fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 7 }}>{children}{req && <span style={{ color: GOLD }}> *</span>}</label>;
}

/* ══════════════════════════════════════════════════════════
   Workshop inquiry form payload — ready for backend
══════════════════════════════════════════════════════════ */
export type WorkshopStatus =
  | 'Nowe' | 'Kontakt z klientem' | 'Ustalamy szczegóły'
  | 'Oferta wysłana' | 'Potwierdzone' | 'Odrzucone' | 'Zrealizowane' | 'Zamknięte';

export interface WorkshopInquiry {
  id?:         string;
  createdAt?:  string;
  status?:     WorkshopStatus;
  type:        'Warsztaty florystyczne';
  /* fields */
  workshopKind: string;
  participants: string;
  occasion:    string;
  topic:       string;
  preferredDate: string;
  budget:      string;
  message:     string;
  fullName:    string;
  phone:       string;
  email:       string;
}

const EMPTY_FORM: WorkshopInquiry = {
  type: 'Warsztaty florystyczne',
  workshopKind: '', participants: '', occasion: '', topic: '',
  preferredDate: '', budget: '', message: '', fullName: '', phone: '', email: '',
};

/* ── Inquiry Form ────────────────────────────────────────── */
function InquiryForm() {
  const [form, setForm] = useState<WorkshopInquiry>({ ...EMPTY_FORM });
  const [sent, setSent]  = useState(false);
  const set = (k: keyof WorkshopInquiry, v: string) => setForm(f => ({ ...f, [k]: v }));

  const canSend = form.workshopKind && form.fullName.trim() && isValidInternationalPhone(form.phone) && form.email.trim();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
      const res = await fetch(`${BASE}/api/workshop-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workshopKind:  form.workshopKind,
          participants:  form.participants,
          occasion:      form.occasion,
          topic:         form.topic,
          preferredDate: form.preferredDate,
          budget:        form.budget,
          message:       form.message,
          fullName:      form.fullName,
          phone:         form.phone,
          email:         form.email,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? WARSZTATY_CONTENT.form.genericServerErrorMessage);
      setSent(true);
    } catch (err: any) {
      setSubmitError(err.message ?? WARSZTATY_CONTENT.form.genericSubmitErrorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bk-fade-up" style={{ textAlign: 'center', padding: 'clamp(36px,5vw,56px) 0' }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', border: `1.5px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
          <CheckCircle2 size={24} color={GOLD} />
        </div>
        <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.6rem,2.8vw,2.3rem)', fontWeight: 400, color: DARK, margin: '0 0 12px', lineHeight: 1.2 }}>
          {WARSZTATY_CONTENT.form.successHeading}
        </h3>
        <p style={{ fontFamily: 'DM Sans', fontSize: 14.5, color: MUTED, lineHeight: 1.85, margin: '0 0 28px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
          {WARSZTATY_CONTENT.form.successBody}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '12px 26px', textDecoration: 'none' }}>
            {WARSZTATY_CONTENT.form.backToShopLabel}
          </Link>
          <button onClick={() => { setForm({ ...EMPTY_FORM }); setSent(false); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.22)`, background: 'none', padding: '12px 26px', cursor: 'pointer' }}>
            {WARSZTATY_CONTENT.form.sendAnotherLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: 16 }}>
        <div>
          <FL req>{WARSZTATY_CONTENT.form.workshopKindLabel}</FL>
          <FSelect value={form.workshopKind} onChange={e => set('workshopKind', e.target.value)} required>
            <option value="">{WARSZTATY_CONTENT.form.selectPlaceholder}</option>
            {WARSZTATY_CONTENT.form.workshopKindOptions.map(o => <option key={o} value={o}>{o.replace(/\u2014/g, '-')}</option>)}
          </FSelect>
        </div>
        <div>
          <FL>{WARSZTATY_CONTENT.form.participantsLabel}</FL>
          <FInput type="number" min={1} value={form.participants} onChange={e => set('participants', e.target.value)} placeholder={WARSZTATY_CONTENT.form.participantsPlaceholder} />
        </div>
      </div>

      {/* Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: 16 }}>
        <div>
          <FL>{WARSZTATY_CONTENT.form.occasionLabel}</FL>
          <FSelect value={form.occasion} onChange={e => set('occasion', e.target.value)}>
            <option value="">{WARSZTATY_CONTENT.form.selectPlaceholder}</option>
            {WARSZTATY_CONTENT.form.occasionOptions.map(o => <option key={o}>{o}</option>)}
          </FSelect>
        </div>
        <div>
          <FL>{WARSZTATY_CONTENT.form.topicLabel}</FL>
          <FSelect value={form.topic} onChange={e => set('topic', e.target.value)}>
            <option value="">{WARSZTATY_CONTENT.form.selectPlaceholder}</option>
            {WARSZTATY_CONTENT.form.topicOptions.map(o => <option key={o}>{o}</option>)}
          </FSelect>
        </div>
      </div>

      {/* Row 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: 16 }}>
        <div>
          <FL>{WARSZTATY_CONTENT.form.preferredDateLabel}</FL>
          <FInput type="date" value={form.preferredDate} onChange={e => set('preferredDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
        </div>
        <div>
          <FL>{WARSZTATY_CONTENT.form.budgetLabel}</FL>
          <FInput value={form.budget} onChange={e => set('budget', e.target.value)} placeholder={WARSZTATY_CONTENT.form.budgetPlaceholder} />
        </div>
      </div>

      {/* Message */}
      <div>
        <FL>{WARSZTATY_CONTENT.form.messageLabel}</FL>
        <FTextarea value={form.message} onChange={e => set('message', e.target.value)} placeholder={WARSZTATY_CONTENT.form.messagePlaceholder} rows={4} />
      </div>

      <div style={{ paddingTop: 4, borderTop: `1px solid ${LINE}` }}>
        <p style={{ fontFamily: 'DM Sans', fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED, margin: '0 0 14px' }}>{WARSZTATY_CONTENT.form.contactSectionLabel}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap: 16 }}>
          <div><FL req>{WARSZTATY_CONTENT.form.fullNameLabel}</FL><FInput value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder={WARSZTATY_CONTENT.form.fullNamePlaceholder} required /></div>
          <div><FL req>{WARSZTATY_CONTENT.form.phoneLabel}</FL><PhoneInput value={form.phone} onChange={value => set('phone', value)} placeholder={WARSZTATY_CONTENT.form.phonePlaceholder} required
            error={form.phone ? phoneValidationMessage(form.phone, true) ?? undefined : undefined} /></div>
          <div><FL req>{WARSZTATY_CONTENT.form.emailLabel}</FL><FInput type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder={WARSZTATY_CONTENT.form.emailPlaceholder} required /></div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <p style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, lineHeight: 1.65, margin: 0 }}>
            {WARSZTATY_CONTENT.form.disclaimer}
          </p>
          {submitError && (
            <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: '#b91c1c', margin: '10px 0 0', lineHeight: 1.6 }}>{submitError}</p>
          )}
        </div>
        <button type="submit" disabled={!canSend || submitting} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: (canSend && !submitting) ? DARK : `${DARK}55`, padding: '13px 30px', border: 'none', cursor: (canSend && !submitting) ? 'pointer' : 'default', flexShrink: 0 }}>
          {submitting ? WARSZTATY_CONTENT.form.submitLabelSending : <>{WARSZTATY_CONTENT.form.submitLabel} <ArrowRight size={13} /></>}
        </button>
      </div>
    </form>
  );
}

/* ════════════════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════════════════ */
export default function WarszytatyPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.warsztatyFlorystyczne);
  const formRef = useRef<HTMLElement>(null);
  const { ref: refS1, inView: inS1 } = useInView<HTMLElement>();
  const { ref: refS2, inView: inS2 } = useInView<HTMLElement>();
  const { ref: refS3, inView: inS3 } = useInView<HTMLElement>();
  const { ref: refS4, inView: inS4 } = useInView<HTMLElement>();
  const { ref: refS5, inView: inS5 } = useInView<HTMLElement>();
  const { ref: refS6, inView: inS6 } = useInView<HTMLElement>();
  const { ref: refS7, inView: inS7 } = useInView<HTMLElement>();
  const { ref: refS9, inView: inS9 } = useInView<HTMLElement>();
  const { ref: refS10, inView: inS10 } = useInView<HTMLElement>();

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const cmsFaq = useCmsFaq(WARSZTATY_CONTENT.faq.items);
  const jsonLd = buildFaqJsonLd(cmsFaq.items);

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>

      <CmsSchema fields={cmsFaq.fields} />
      <script data-cms-jsonld="faq" data-cms-faq-prefix="information.faq.items" type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(jsonLd) }} />
      <PageBreadcrumb crumbs={[{ label: 'Warsztaty florystyczne' }]} />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370, margin: '0 auto', padding: 'max(clamp(24px, 3vw, 48px), calc(var(--page-breadcrumb-height, 44px) + 12px)) var(--page-gutter) clamp(24px, 3vw, 48px)' }}>
          <div className="bk-fade-in bk-d1" style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>

            <p className="bk-fade-up bk-d1" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', color: GOLD, marginBottom: 16 }}>
              <CmsEditorialText id="information.hero.kicker" value={WARSZTATY_CONTENT.hero.kicker} section="Wstęp" label="Treść sekcji" />
            </p>


            <p className="bk-fade-up bk-d2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: BROWN, marginBottom: 16, fontStyle: 'italic' }}>
              <CmsEditorialText id="information.hero.tagline" value={WARSZTATY_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
            </p>

            <h1 className="bk-fade-up bk-d3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)', fontWeight: 400, lineHeight: 1.0, color: DARK, margin: '0 0 28px' }}>
              <CmsEditorialText id="information.hero.headingLine1" value={WARSZTATY_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br /><CmsEditorialText id="information.hero.headingLine2" value={WARSZTATY_CONTENT.hero.headingLine2} section="Wstęp" label="Nagłówek — druga linia" />
            </h1>

            <p className="bk-fade-up bk-d4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: BROWN, lineHeight: 1.85, maxWidth: 540, margin: '0 auto 44px' }}>
              <CmsEditorialText id="information.hero.body" value={WARSZTATY_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
            </p>

            <div className="bk-fade-up bk-d5" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button onClick={scrollToForm}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '15px 32px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.85')}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
              >
                <CmsEditorialText id="information.hero.ctaAskLabel" value={WARSZTATY_CONTENT.hero.ctaAskLabel} section="Wstęp" label="Tekst przycisku" />
                <ArrowRight size={13} />
              </button>
              <a href="#co-stworzymy"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, textDecoration: 'none', border: `1px solid ${GOLD}88`, padding: '15px 32px', transition: 'border-color 0.2s', cursor: 'pointer' }}
                onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
                onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = `${GOLD}88`)}
              >
                <CmsEditorialText id="information.hero.ctaSeeLabel" value={WARSZTATY_CONTENT.hero.ctaSeeLabel} section="Wstęp" label="Tekst przycisku" />
                <ArrowRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── NIE MUSISZ BYĆ FLORYSTĄ ───────────────────────── */}
      <section ref={refS1} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,420px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>
            <div style={{ opacity: inS1 ? undefined : 0 }} className={inS1 ? 'bk-fade-up' : undefined}>
              <SectionLabel text={WARSZTATY_CONTENT.notFlorist.eyebrow} />
              <H2><CmsEditorialText id="information.notFlorist.headingLine1" value={WARSZTATY_CONTENT.notFlorist.headingLine1} section="Dla początkujących" label="Nagłówek — pierwsza linia" /><br /><CmsEditorialText id="information.notFlorist.headingLine2" value={WARSZTATY_CONTENT.notFlorist.headingLine2} section="Dla początkujących" label="Nagłówek — druga linia" /></H2>

              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, lineHeight: 1.9, margin: '0 0 16px' }}>
                <CmsEditorialText id="information.notFlorist.body1" value={WARSZTATY_CONTENT.notFlorist.body1} section="Dla początkujących" label="Treść sekcji" multiline />
              </p>
              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, lineHeight: 1.9, margin: 0 }}>
                <CmsEditorialText id="information.notFlorist.body2" value={WARSZTATY_CONTENT.notFlorist.body2} section="Dla początkujących" label="Treść sekcji" multiline />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DWA SPOSOBY ───────────────────────────────────── */}
      <section ref={refS2} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ opacity: inS2 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,56px)' }} className={inS2 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.format.eyebrow} />
            <H2><CmsEditorialText id="information.format.heading" value={WARSZTATY_CONTENT.format.heading} section="Rodzaje warsztatów" label="Nagłówek" /></H2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,380px),1fr))', gap: 'clamp(16px,2vw,24px)' }}>
            {/* Indywidualne */}
            <div style={{ opacity: inS2 ? undefined : 0, animationDelay: '0.06s', background: '#fff', padding: 'clamp(32px,4vw,52px)', borderTop: `2px solid ${GOLD}`, display: 'flex', flexDirection: 'column' }} className={inS2 ? 'bk-fade-up' : undefined}>
              <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD, margin: '0 0 12px' }}><CmsEditorialText id="information.format.individual.tag" value={WARSZTATY_CONTENT.format.individual.tag} section="Rodzaje warsztatów" label="nadtytuł" /></p>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.1 }}><CmsEditorialText id="information.format.individual.title" value={WARSZTATY_CONTENT.format.individual.title} section="Rodzaje warsztatów" label="tytuł" /></h3>
              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: '0 0 24px' }}>
                <CmsEditorialText id="information.format.individual.body" value={WARSZTATY_CONTENT.format.individual.body} section="Rodzaje warsztatów" label="opis" multiline />
              </p>
              <ul style={{ margin: '0 0 28px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {WARSZTATY_CONTENT.format.individual.bullets.map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1px solid ${GOLD}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: GOLD }} />
                    </div>
                    <span style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED, lineHeight: 1.6 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.25)`, background: 'none', padding: '12px 22px', cursor: 'pointer', marginTop: 'auto', width: 'fit-content' }}>
                {WARSZTATY_CONTENT.format.individual.ctaLabel} <ArrowRight size={12} />
              </button>
            </div>

            {/* Grupowo */}
            <div style={{ opacity: inS2 ? undefined : 0, animationDelay: '0.1s', background: '#fff', padding: 'clamp(32px,4vw,52px)', borderTop: `2px solid ${GOLD}`, display: 'flex', flexDirection: 'column' }} className={inS2 ? 'bk-fade-up' : undefined}>
              <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD, margin: '0 0 12px' }}><CmsEditorialText id="information.format.group.tag" value={WARSZTATY_CONTENT.format.group.tag} section="Rodzaje warsztatów" label="nadtytuł" /></p>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.1 }}><CmsEditorialText id="information.format.group.title" value={WARSZTATY_CONTENT.format.group.title} section="Rodzaje warsztatów" label="tytuł" /></h3>
              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: '0 0 24px' }}>
                <CmsEditorialText id="information.format.group.body" value={WARSZTATY_CONTENT.format.group.body} section="Rodzaje warsztatów" label="opis" multiline />
              </p>
              <ul style={{ margin: '0 0 28px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {WARSZTATY_CONTENT.format.group.bullets.map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1px solid ${GOLD}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: GOLD }} />
                    </div>
                    <span style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED, lineHeight: 1.6 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD, border: `1px solid ${GOLD}55`, background: 'none', padding: '12px 22px', cursor: 'pointer', marginTop: 'auto', width: 'fit-content' }}>
                {WARSZTATY_CONTENT.format.group.ctaLabel} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CO STWORZYMY RAZEM ────────────────────────────── */}
      <section ref={refS3} id="co-stworzymy" style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ opacity: inS3 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS3 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.whatWeCreate.eyebrow} />
            <H2><CmsEditorialText id="information.whatWeCreate.heading" value={WARSZTATY_CONTENT.whatWeCreate.heading} section="Co tworzymy" label="Nagłówek" /></H2>
          </div>

          {/* Editorial asymmetric grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 'clamp(10px,1.5vw,16px)' }}>
            {WARSZTATY_CONTENT.whatWeCreate.types.map((wt, i) => {
              const spans = ['span 7','span 5','span 5','span 7','span 6','span 6'];
              const Icon = ICON_MAP[wt.iconKey];
              return (
                <div key={wt.label} style={{ opacity: inS3 ? undefined : 0, animationDelay: `${i * 0.07}s`, gridColumn: spans[i] ?? 'span 6' }} className={inS3 ? 'bk-fade-up' : undefined}>
                  <div style={{ background: '#fff', padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: 'clamp(18px,2.5vw,28px)', borderTop: `1px solid ${LINE}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <Icon size={13} color={GOLD} />
                        <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, margin: 0 }}><CmsEditorialText id={`information.whatWeCreate.types.${i}.label`} value={wt.label} section="Co tworzymy" label={`Element ${i + 1}: tekst`} /></p>
                      </div>
                      <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, lineHeight: 1.7, margin: 0 }}><CmsEditorialText id={`information.whatWeCreate.types.${i}.desc`} value={wt.desc} section="Co tworzymy" label={`Element ${i + 1}: opis`} multiline /></p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ opacity: inS3 ? undefined : 0, animationDelay: '0.1s', marginTop: 28, background: '#fff', padding: 'clamp(20px,2.5vw,28px)', borderLeft: `2px solid ${GOLD}` }} className={inS3 ? 'bk-fade-up' : undefined}>
            <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.8, margin: 0 }}>
              <CmsEditorialText id="information.whatWeCreate.noteText" value={WARSZTATY_CONTENT.whatWeCreate.noteText} section="Co tworzymy" label="Treść sekcji" multiline />{' '}
              <button onClick={scrollToForm} style={{ fontFamily: 'DM Sans', fontSize: 'inherit', color: GOLD, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                <CmsEditorialText id="information.whatWeCreate.noteLinkLabel" value={WARSZTATY_CONTENT.whatWeCreate.noteLinkLabel} section="Co tworzymy" label="Tekst przycisku" multiline />
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* ── SZYTE NA MIARĘ ────────────────────────────────── */}
      <section ref={refS4} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,400px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>
            <div style={{ opacity: inS4 ? undefined : 0 }} className={inS4 ? 'bk-fade-up' : undefined}>
              <p style={{ fontFamily: 'DM Sans', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: '0 0 14px' }}><CmsEditorialText id="information.custom.eyebrow" value={WARSZTATY_CONTENT.custom.eyebrow} section="Indywidualne warsztaty" label="Nadtytuł" /></p>
              <H2 style={{ color: DARK, fontSize: 'clamp(2rem,3.8vw,3.2rem)' }}>
                <CmsEditorialText id="information.custom.headingLine1" value={WARSZTATY_CONTENT.custom.headingLine1} section="Indywidualne warsztaty" label="Nagłówek — pierwsza linia" /><br />
                <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.custom.headingAccent" value={WARSZTATY_CONTENT.custom.headingAccent} section="Indywidualne warsztaty" label="Wyróżniona część nagłówka" /></em>
              </H2>

              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.9, margin: '0 0 28px' }}>
                <CmsEditorialText id="information.custom.body" value={WARSZTATY_CONTENT.custom.body} section="Indywidualne warsztaty" label="Opis" multiline />
              </p>
              <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, background: CREAM, padding: '13px 26px', border: 'none', cursor: 'pointer' }}>
                <CmsEditorialText id="information.custom.ctaLabel" value={WARSZTATY_CONTENT.custom.ctaLabel} section="Indywidualne warsztaty" label="Tekst przycisku" /> <ArrowRight size={13} />
              </button>
            </div>

            <div style={{ opacity: inS4 ? undefined : 0, animationDelay: '0.1s', display: 'flex', flexDirection: 'column', gap: 10 }} className={inS4 ? 'bk-fade-up' : undefined}>
              {WARSZTATY_CONTENT.custom.quickLinks.map((label, i) => (
                <button key={label} onClick={scrollToForm}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'clamp(15px,2vw,20px) clamp(16px,2vw,24px)', background: '#fff', border: `1px solid ${LINE}`, cursor: 'pointer', transition: 'background 0.2s', animationDelay: `${0.08 + i * 0.05}s` }}
                  onMouseEnter={e => (e.currentTarget.style.background = `${GOLD}08`)}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >
                  <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px,1.7vw,20px)', fontWeight: 400, color: DARK }}><CmsEditorialText id={`information.custom.quickLinks.${i}.text`} value={label} section="Indywidualne warsztaty" label={`Element ${i + 1}: tekst`} multiline /></span>
                  <ArrowRight size={14} color={`${GOLD}88`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CO OTRZYMUJESZ ────────────────────────────────── */}
      <section ref={refS5} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ opacity: inS5 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS5 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.includes.eyebrow} />
            <H2><CmsEditorialText id="information.includes.heading" value={WARSZTATY_CONTENT.includes.heading} section="W cenie warsztatu" label="Nagłówek" /></H2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,290px),1fr))', gap: 1, background: LINE }}>
            {WARSZTATY_CONTENT.includes.items.map((item, i) => {
              const Icon = ICON_MAP[item.iconKey];
              return (
                <div key={item.title} style={{ opacity: inS5 ? undefined : 0, animationDelay: `${i * 0.07}s`, background: '#fff', padding: 'clamp(24px,3vw,38px)' }} className={inS5 ? 'bk-fade-up' : undefined}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                    <Icon size={16} color={GOLD} />
                  </div>
                  <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(17px,1.8vw,21px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.2 }}><CmsEditorialText id={`information.includes.items.${i}.title`} value={item.title} section="W cenie warsztatu" label={`Element ${i + 1}: tytuł`} /></h3>
                  <p style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED, lineHeight: 1.75, margin: 0 }}><CmsEditorialText id={`information.includes.items.${i}.desc`} value={item.desc} section="W cenie warsztatu" label={`Element ${i + 1}: opis`} multiline /></p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── DLA KOGO ──────────────────────────────────────── */}
      <section ref={refS6} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ opacity: inS6 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS6 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.forWhom.eyebrow} />
            <H2><CmsEditorialText id="information.forWhom.heading" value={WARSZTATY_CONTENT.forWhom.heading} section="Dla kogo" label="Nagłówek" /></H2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,180px),1fr))', gap: 1, background: LINE, marginBottom: 28 }}>
            {WARSZTATY_CONTENT.forWhom.items.map((label, i) => (
              <button key={label} onClick={scrollToForm}
                style={{ opacity: inS6 ? undefined : 0, animationDelay: `${i * 0.05}s`, background: CREAM, padding: 'clamp(22px,3vw,32px)', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                className={inS6 ? 'bk-fade-up' : undefined}
                onMouseEnter={e => (e.currentTarget.style.background = CREAM3)}
                onMouseLeave={e => (e.currentTarget.style.background = CREAM)}
              >
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(18px,1.9vw,23px)', fontWeight: 400, color: DARK, margin: '0 0 8px', lineHeight: 1.2 }}><CmsEditorialText id={`information.forWhom.items.${i}.text`} value={label} section="Dla kogo" label={`Element ${i + 1}: tekst`} multiline /></h3>
                <span style={{ fontFamily: 'DM Sans', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}><CmsEditorialText id="information.forWhom.ctaLabel" value={WARSZTATY_CONTENT.forWhom.ctaLabel} section="Dla kogo" label="Tekst przycisku" /></span>
              </button>
            ))}
          </div>
          <div style={{ opacity: inS6 ? undefined : 0, animationDelay: '0.1s' }} className={inS6 ? 'bk-fade-up' : undefined}>
            <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.35vw,15px)', color: MUTED, lineHeight: 1.85, margin: 0 }}>
              <CmsEditorialText id="information.forWhom.footerNote" value={WARSZTATY_CONTENT.forWhom.footerNote} section="Dla kogo" label="Informacja dodatkowa" multiline />
            </p>
          </div>
        </div>
      </section>

      {/* ── JAK WYGLĄDAJĄ WARSZTATY ───────────────────────── */}
      <section ref={refS7} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
          <div style={{ opacity: inS7 ? undefined : 0, marginBottom: 'clamp(36px,4.5vw,52px)' }} className={inS7 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.process.eyebrow} />
            <H2><CmsEditorialText id="information.process.heading" value={WARSZTATY_CONTENT.process.heading} section="Jak to działa" label="Nagłówek" /></H2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,190px),1fr))', gap: 1, background: LINE }}>
            {WARSZTATY_CONTENT.process.steps.map((s, i) => (
              <div key={s.n} style={{ opacity: inS7 ? undefined : 0, animationDelay: `${i * 0.07}s`, background: '#fff', padding: 'clamp(24px,3vw,36px)', borderTop: i === 0 ? `2px solid ${GOLD}` : 'none' }} className={inS7 ? 'bk-fade-up' : undefined}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.2rem,3.5vw,3.2rem)', fontWeight: 400, color: `${GOLD}28`, lineHeight: 1, margin: '0 0 18px' }}>{s.n}</p>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px,1.6vw,19px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.3 }}><CmsEditorialText id={`information.process.steps.${i}.title`} value={s.title} section="Jak to działa" label={`Element ${i + 1}: tytuł`} /></h3>
                <p style={{ fontFamily: 'DM Sans', fontSize: 12.5, color: MUTED, lineHeight: 1.8, margin: 0 }}><CmsEditorialText id={`information.process.steps.${i}.note`} value={s.note} section="Jak to działa" label={`Element ${i + 1}: opis`} multiline /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GALERIA ───────────────────────────────────────── */}

      {/* ── DLA FIRM ──────────────────────────────────────── */}
      <section ref={refS9} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,380px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>
            <div style={{ opacity: inS9 ? undefined : 0, animationDelay: '0.1s' }} className={inS9 ? 'bk-fade-up' : undefined}>
              <SectionLabel text={WARSZTATY_CONTENT.b2b.eyebrow} />
              <H2>{WARSZTATY_CONTENT.b2b.headingLine1}<br />{WARSZTATY_CONTENT.b2b.headingLine2}</H2>

              <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.9, margin: '0 0 24px' }}>
                {WARSZTATY_CONTENT.b2b.body}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button onClick={scrollToForm} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '13px 26px', border: 'none', cursor: 'pointer' }}>
                  {WARSZTATY_CONTENT.b2b.ctaAskLabel} <ArrowRight size={13} />
                </button>
                <Link href="/dla-firm/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.22)`, padding: '13px 22px', textDecoration: 'none' }}>
                  {WARSZTATY_CONTENT.b2b.ctaOfferLabel} <ChevronRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section ref={refS10} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 clamp(24px,5vw,48px)' }}>
          <div style={{ opacity: inS10 ? undefined : 0, marginBottom: 'clamp(32px,4vw,44px)' }} className={inS10 ? 'bk-fade-up' : undefined}>
            <SectionLabel text={WARSZTATY_CONTENT.faq.eyebrow} />
            <H2><CmsEditorialText id="information.faq.heading" value={WARSZTATY_CONTENT.faq.heading} section="Pytania i odpowiedzi" label="Nagłówek" /></H2>
          </div>
          <div style={{ opacity: inS10 ? undefined : 0, animationDelay: '0.08s' }} className={inS10 ? 'bk-fade-up' : undefined}>
            {cmsFaq.items.map((item, i) => <FaqAccordionItem key={i} q={item.q} a={item.a} cmsKey={item.cmsKey} />)}
          </div>
          {/* Internal links */}
          <div style={{ opacity: inS10 ? undefined : 0, animationDelay: '0.1s', marginTop: 36, paddingTop: 24, borderTop: `1px solid ${LINE}`, display: 'flex', gap: 16, flexWrap: 'wrap' }} className={inS10 ? 'bk-fade-up' : undefined}>
            {WARSZTATY_CONTENT.faq.quickLinks.map(link => (
              <Link key={link.href} href={link.href} style={{ fontFamily: 'DM Sans', fontSize: 12, color: GOLD, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>{link.label} <ArrowRight size={11} /></Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORMULARZ CTA ─────────────────────────────────── */}
      <section ref={formRef} style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0', borderTop: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>
          <div className="bk-fade-up" style={{ marginBottom: 'clamp(36px,4.5vw,48px)' }}>
            <SectionLabel text={WARSZTATY_CONTENT.formCta.eyebrow} />
            <H2><CmsEditorialText id="information.formCta.heading" value={WARSZTATY_CONTENT.formCta.heading} section="Zachęta do kontaktu" label="Nagłówek" /></H2>
            <p style={{ fontFamily: 'DM Sans', fontSize: 'clamp(13.5px,1.3vw,15px)', color: MUTED, lineHeight: 1.85, margin: '14px 0 0', maxWidth: 560 }}>
              <CmsEditorialText id="information.formCta.body" value={WARSZTATY_CONTENT.formCta.body} section="Zachęta do kontaktu" label="Opis" multiline />
            </p>
          </div>
          <div className="bk-fade-up bk-d2" style={{ background: '#fff', padding: 'clamp(28px,4vw,52px)' }}>
            <InquiryForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
